<template>
  <communication-log-card
    background-class="bg-turquoise"
    :comments="log.data.comments"
  >
    <template #icon>
      <div
        class="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-turquoise"
      >
        <icon-base :icon="IconMessage" class="text-white" />
        <div
          class="absolute -right-2-5 -bottom-2-5 flex h-6 w-6 items-center justify-center rounded-full border-2 border-disabled"
          :class="log.data.is_outgoing ? 'bg-primary' : 'bg-turquoise'"
        >
          <icon-base
            :icon="IconIncomingArrow"
            height="12"
            width="12"
            class="text-white"
            :class="{ 'rotate-180 transform': log.data.is_outgoing }"
          />
        </div>
      </div>
    </template>
    <template #content>
      <div class="flex flex-col space-y-2 border-b text-gray-500">
        <div
          class="sms-header rounded-t-md bg-grey-hover px-4 py-2 font-medium"
        >
          <business-name
            :id="log.data.id"
            :name="log.data.smsable?.business?.business_legal_name"
            :application-id="applicationId"
            :type="CommunicationType.sms"
            class="w-full"
            @click.stop
          />
        </div>
        <div class="space-y-3 px-6 py-4">
          <log-quick-submit
            v-if="isReplying"
            v-model="message"
            class="quick-submit-form"
            :placeholder="$t('ACTIVITY.SMS.MESSAGE_PLACEHOLDER')"
            :return-placeholder="$t('ACTIVITY.SMS.RETURN_PLACEHOLDER')"
            :loading="loading"
            @submit="sendMessage"
          >
            <template #actionBar>
              <sms-from-number :prepend="$t('COMMON.FROM')" />
            </template>
          </log-quick-submit>
          <div v-else class="flex w-full space-x-3">
            <div
              class="flex-1 whitespace-pre-wrap text-label"
              data-cy="sms-message"
            >
              {{ log.data.message }}
            </div>
            <action-menu-wrapper
              v-if="!log.data.is_outgoing"
              :items="menuItems"
              :offset="offset"
              teleport
            />
          </div>
          <div v-if="log.data.status_message" class="flex space-x-1">
            <icon-base :icon="IconErrorWarning" class="text-error" />
            <span class="text-error">{{ log.data.status_message }}</span>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <div v-if="log" class="flex w-full justify-between space-y-1 bg-white">
        <div class="flex-row-full-justify-between wrapper">
          <div class="wrap-children flex items-center space-x-2">
            <sms-status v-if="log.data.status" :status="log.data.status" />
            <avatar-with-name
              v-if="sender.id"
              :user="getName(sender.name)"
              data-cy="sms-sender"
            />
            <span v-if="senderPhoneNumber">
              {{ senderPhoneNumber }}
            </span>
            <icon-base
              :icon="IconIncomingArrow"
              height="12"
              width="12"
              :class="[
                log.data.is_outgoing
                  ? 'rotate-180 transform text-primary'
                  : 'text-turquoise',
              ]"
            />
            <span
              v-if="log.data.receiver.id || (log.data.to && log.data.from)"
              class="text-xs font-medium"
            >
              {{
                log.data.is_outgoing
                  ? $t("ACTIVITY.SMS.TO")
                  : $t("ACTIVITY.SMS.FROM")
              }}
            </span>
            <avatar-with-name
              v-if="recipient.id"
              :user="getName(recipient.name)"
              data-cy="sms-receiver"
            />
            <span v-if="recipientPhoneNumber">
              {{ recipientPhoneNumber }}
            </span>
            <links-clicked-tooltip-icon
              v-if="!!log.data.link_clicked_events?.length"
              data-cy="sms-links-clicked"
            >
              <div
                class="flex flex-col space-y-1"
                data-cy="sms-links-clicked-detail"
              >
                <div
                  v-for="datetime in log.data.link_clicked_events"
                  :key="datetime"
                  class="max-w-200 truncate text-xxs text-white/80"
                >
                  <span class="text-white">
                    {{
                      formatDateCustom(datetime, "MMM dd, yyyy hh:mm a", true)
                    }}
                  </span>
                </div>
              </div>
            </links-clicked-tooltip-icon>
            <span v-if="log.data?.created_at">&#8226;</span>
            <date-time :date="log.data?.created_at" />
          </div>
        </div>
      </div>
    </template>
  </communication-log-card>
</template>
<script lang="ts" setup>
import { computed, provide, ref } from "vue";
import { useI18n } from "vue-i18n";

import { LogEndpointType } from "@/enums/communicationLogs";
import { CommunicationType } from "@/enums/communicationLogs";
import { getAssignedPhoneNumber } from "@/helpers/communication";
import { COMMON_MENU_OFFSET } from "@/helpers/constants";
import { formatDateCustom } from "@/helpers/formatting";
import { CommunicationLogContextKey } from "@/helpers/symbols";
import { usePromiseWrapper } from "@/hooks/common";
import { useNotification } from "@/hooks/notifications";
import { useLocalStorageSetting } from "@/hooks/options";
import communicationService from "@/services/modules/communication";

import CommunicationLogCard from "@/components/communicationLogs/communication/CommunicationLogCard.vue";
import BusinessName from "@/components/communicationLogs/communication/emailThread/BusinessName.vue";
import LinksClickedTooltipIcon from "@/components/communicationLogs/communication/emailThread/LinksClickedTooltipIcon.vue";
import SmsFromNumber from "@/components/communicationLogs/communication/sms/SmsFromNumber.vue";
import SmsStatus from "@/components/communicationLogs/communication/sms/SmsStatus.vue";
import LogQuickSubmit from "@/components/communicationLogs/forms/LogQuickSubmit.vue";
import IconErrorWarning from "@/components/icons/IconErrorWarning.vue";
import IconIncomingArrow from "@/components/icons/IconIncomingArrow.vue";
import IconMessage from "@/components/icons/IconMessage.vue";
import IconSend from "@/components/icons/IconSend.vue";
import ActionMenuWrapper from "@/components/ui/ActionMenuWrapperV2.vue";
import AvatarWithName from "@/components/ui/AvatarWithName.vue";
import DateTime from "@/components/ui/ConvertibleDateTime.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type { MenuItem } from "@/components/ui/ActionMenuWrapperV2.vue";
import type { SmsLog } from "@/models/communicationLogs";

interface Props {
  log: SmsLog;
}

const { log } = defineProps<Props>();

const offset = COMMON_MENU_OFFSET;

provide(CommunicationLogContextKey, {
  type: LogEndpointType.Sms,
  logId: log.data.id,
});

const { t } = useI18n();
const { showMessage } = useNotification();
const assignedPhoneNumber = useLocalStorageSetting("assignedPhoneNumber", null);

const isReplying = ref(false);
const message = ref("");

const menuItems = computed<MenuItem[]>(() => [
  {
    label: t("COMMON.REPLY"),
    handler: () => (isReplying.value = true),
    icon: IconSend,
    class: "text-label",
  },
]);

const applicationId = computed(() =>
  log?.data?.smsable?.application_id?.toString()
);

const recipient = computed(() => {
  return log.data.is_outgoing ? log.data.receiver : log.data.sender;
});

const sender = computed(() => {
  return log.data.is_outgoing ? log.data.sender : log.data.receiver;
});

const senderPhoneNumber = computed(() =>
  log.data.is_outgoing ? log.data.from : log.data.to
);
const recipientPhoneNumber = computed(() =>
  log.data.is_outgoing ? log.data.to : log.data.from
);

const getName = (name: string | null) => {
  const nameParts = name?.split(" ");
  return { first_name: nameParts?.[0] || "", last_name: nameParts?.[1] || "" };
};

const { loading, fetchWrapper: sendMessage } = usePromiseWrapper(async () => {
  if (!log?.data?.id || !message.value) {
    return;
  }
  const from = getAssignedPhoneNumber(assignedPhoneNumber.value);
  await communicationService.replyToSms(log.data.id, message.value, from);
  isReplying.value = false;
  message.value = "";
  showMessage(t("ACTIVITY.SMS.SUCCESS_MESSAGE"), "success");
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.sms-header :deep() {
  .justify-between.group {
    @apply justify-start space-x-2;
  }
}

:deep(.quick-submit-form) {
  .action-button-wrapper {
    @apply w-full;
  }
}
</style>
